// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — the 278px MULTI-CAPABILITY LEFT SLIDEOUT (Tier-3 fidelity overhaul).
// Mock: Tak-TDF COP.dc.html lines 143–285 (the slideout + its 6 swappable panes).
// Spec: docs/ux/SHELL-COMPOSITION.md §2.1–2.3.
//
// A MAP OVERLAY (NOT a grid track): an absolute sibling inside `.cop` anchored just
// right of the rail (left tracks the rail's live width — 54px collapsed, 196px
// expanded via railState), frosted glass — so the MAP DOES NOT REFLOW when it opens
// (map-primacy preserved). It is summoned by the FunctionRail's buttons via the
// orchestrator's `leftPanel` state and dismissed by the shared Esc stack (LIFO) or
// the header ✕.
//
// Z-ORDER: the slideout takes its z-index from the shared panelStack focus band
// (last-opened/last-clicked wins the front — the pop-under fix) instead of a static
// constant: it RAISES on every pane open/switch and on any pointerdown inside it,
// and DROPS when closed. `data-focused` (front of the band) drives the teal focus
// hairline (leftslideout.css). Rail-summoned popovers (verdict drill, persona pop)
// live on the raised rail layer ABOVE this band — a menu always beats a panel.
//
// Six panes swap in the shared body, each with a shared header (Saira title + a mono
// tag + ✕):
//   • search  — released-object discovery (census-honest count; the search input
//               teaches the omnibox). Glomar: counts the CLEARED-VISIBLE set only.
//   • layers  — the MIGRATED LivingSpine (the live feed ledger + the +Add Catalog
//               flip). Its full behavior is preserved — only WHERE it renders moved.
//   • request — Glomar-compliant discovery (REQUESTABLE language only, never
//               "withheld"). Empty today (requestable=0) → the mock empty state.
//   • fires   — plugin pane (header tag PLUGIN · FIRES-CELL v2.1) — visual shell.
//   • ew      — plugin pane (header tag PLUGIN · SPECTRUM v1.4) — visual shell.
//   • audit   — the append-only KAS verdict ledger. Renders status.audit (the host's
//               OBSERVE-only ring of terminal KAS/PDP verdicts) newest-first as
//               GRANT/DENY/REQUEST rows; honest empty state until the first verdict.
//               KAS verdicts live ONLY here + the Trust modal (rule 4).
//
// React stays OUT of the render loop: reads the immutable CopStatus, calls CopHandle
// and the orchestrator's onClose. No host/MapLibre import.

import { useEffect } from "react";
import type { CopStatus, CopHandle } from "../host/cop.ts";
import type { ChatApi } from "../capabilities/chatCapability.ts";
import { useEscapeStack } from "./useEscapeStack.ts";
import { usePanelLayer } from "./panelStack.ts";
import { useRailExpanded, RAIL_EXPANDED_W, RAIL_COLLAPSED_W } from "./railState.ts";
import { LivingSpine } from "./LivingSpine.tsx";
import { ChatPanel } from "./ChatPanel.tsx";
import { AlertsPanel } from "./AlertsPanel.tsx";
import "./leftslideout.css";

/** The summonable panes (must match FunctionRail's LeftPanel non-null members). */
export type SlideoutPane = "search" | "layers" | "request" | "fires" | "ew" | "audit" | "chat" | "alerts";

export interface LeftSlideoutProps {
  /** The open pane, or null when the slideout is closed (nothing renders). */
  pane: SlideoutPane | null;
  status: CopStatus;
  handle: CopHandle;
  /** The encrypted-chat capability's panel-facing API (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
   *  Threaded from the host (handle.chatApi) so the "chat" pane renders <ChatPanel api={…}/>
   *  inside the slideout — the same narrow seam the right-dock mount used, just re-homed.
   *  Optional so a pre-boot render (handle not ready) simply shows nothing for the chat pane. */
  chatApi?: ChatApi;
  /** Dismiss the slideout (header ✕ + the Esc stack both route here). */
  onClose: () => void;
}

/** Per-pane Saira title (mock titleMap, line 871). */
const TITLE: Record<SlideoutPane, string> = {
  search: "Search",
  layers: "Layers",
  request: "Request Access",
  fires: "Call for Fires",
  ew: "Spectrum · EW",
  audit: "Audit Ledger",
  chat: "Chat",
  alerts: "Alerts",
};

/** Per-pane mono tag label (mock tagMap, line 872); "" → no tag row. */
const TAG: Record<SlideoutPane, string> = {
  search: "",
  layers: "",
  request: "DISCOVERY",
  fires: "PLUGIN · FIRES-CELL v2.1",
  ew: "PLUGIN · SPECTRUM v1.4",
  audit: "APPEND-ONLY",
  chat: "ENCRYPTED · E2E",
  alerts: "",
};

export function LeftSlideout({ pane, status, handle, chatApi, onClose }: LeftSlideoutProps): JSX.Element | null {
  // The slideout is a LIGHT summoned surface: one Esc pops exactly this level (LIFO),
  // only while it's open. A heavy panel inside it (the Catalog flip) registers its own
  // deeper Esc level, so Esc closes the flip first, then the slideout.
  useEscapeStack(onClose, pane !== null);

  // FOCUS/Z-ORDER (the pop-under fix): join the shared panel band. Raise on every
  // open + pane SWITCH (a rail click must bring this panel to the front), drop on
  // close so the next-most-recent surface inherits the front + its focus hairline.
  const layer = usePanelLayer("slideout");
  const { raise, drop } = layer;
  useEffect(() => {
    if (pane !== null) raise();
    else drop();
  }, [pane, raise, drop]);

  // The rail's live width anchors the slideout's left edge (54px collapsed /
  // 196px expanded) so the expanded rail sits BESIDE an open pane, never over it.
  const railExpanded = useRailExpanded();

  if (pane === null) return null;

  const tag = TAG[pane];

  return (
    <aside
      className="slideout"
      aria-label={`${TITLE[pane]} panel`}
      style={{ zIndex: layer.z, left: railExpanded ? RAIL_EXPANDED_W : RAIL_COLLAPSED_W }}
      data-focused={layer.focused ? "" : undefined}
      // Capture-phase so ANY click inside the pane (including on its children)
      // re-raises the surface — "click an already-open panel → it comes to front".
      onPointerDownCapture={raise}
    >
      <header className="slideout__head">
        <div className="slideout__head-titles">
          <div className="slideout__title">{TITLE[pane]}</div>
          {tag && <div className={`slideout__tag slideout__tag--${pane}`}>{tag}</div>}
        </div>
        <button type="button" className="slideout__close" aria-label="Close panel" onClick={onClose}>
          ✕
        </button>
      </header>

      <div className="slideout__body">
        {pane === "search" && <SearchPane status={status} handle={handle} />}
        {/* LAYERS — the migrated LivingSpine. It fills the body and keeps its +Add
            Catalog flip, eye/opacity/zoom/remove controls, and the `A` shortcut. */}
        {pane === "layers" && <LivingSpine status={status} handle={handle} />}
        {pane === "request" && <RequestPane status={status} />}
        {pane === "fires" && <FiresPane />}
        {pane === "ew" && <EwPane />}
        {pane === "audit" && <AuditPane status={status} />}
        {/* ALERTS — the acknowledgeable alert log (mark-read on click + clear-all). Pure React
            over the CopStatus snapshot; onSelect fly+selects the alerting (visible) entity. */}
        {pane === "alerts" && (
          <AlertsPanel
            status={status}
            handle={handle}
            onSelect={(id) => {
              handle.select(id);
              handle.locate(id);
            }}
          />
        )}
        {/* CHAT — the re-homed encrypted mission-room chat. <ChatPanel> is a PURE React
            component over the narrow ChatApi (no engine/crypto here); it fills the body and
            keeps its room switcher, [TDF] composer, roster, and the §5.3 plaintext bridge.
            Renders only once the host's chatApi is threaded (post-boot). */}
        {pane === "chat" && (chatApi ? <ChatPanel api={chatApi} /> : <ChatPending />)}
      </div>
    </aside>
  );
}

// ── SEARCH pane ───────────────────────────────────────────────────────────────
// Honest discovery over the CLEARED-VISIBLE set: the released count is the census
// value (Glomar — never a hidden universe). The search box teaches the omnibox (the
// real fan-out search is the Throughline `/` rung); here it is a discoverable entry.
function SearchPane({ status, handle }: { status: CopStatus; handle: CopHandle }): JSX.Element {
  const released = status.census.released;
  void handle; // reserved: a future build wires per-object rows off a host list seam.
  return (
    <div className="slideout-search">
      <div className="slideout-search__box">
        <span className="slideout-search__icon" aria-hidden="true">
          ⌕
        </span>
        <span className="slideout-search__hint">callsign, kind, MGRS…</span>
      </div>
      <div className="slideout-search__count">{released} OBJECTS RELEASED TO YOU</div>
      <div className="slideout-search__teach">
        Press <kbd>/</kbd> to search the picture from the omnibox — every match is an
        object already released to you.
      </div>
    </div>
  );
}

// ── REQUEST pane (Glomar-compliant discovery) ─────────────────────────────────
// REQUESTABLE language ONLY — never "withheld". Today requestable=0, so the mock's
// empty state shows. The footer reiterates the Glomar contract verbatim from the
// mock (lines 207–214). NO deny/registry path.
function RequestPane({ status }: { status: CopStatus }): JSX.Element {
  const requestable = status.census.requestable;
  return (
    <div className="slideout-request">
      <p className="slideout-request__intro">
        Datasets the owner advertises to you. Request access to bring one into your
        picture — anything outside your clearance is invisible by design.
      </p>

      {requestable === 0 ? (
        <div className="slideout-request__empty">
          <div className="slideout-request__empty-title">Nothing to request</div>
          <div className="slideout-request__empty-body">
            Everything available to you is already on the map.
          </div>
        </div>
      ) : null}

      <div className="slideout-request__foot">
        Only datasets the owner advertises appear here. Anything outside your clearance
        is invisible by design.
      </div>
    </div>
  );
}

// ── FIRES plugin pane (visual shell) ──────────────────────────────────────────
// Published by the Fires-Cell plugin (plugin-owned chrome). A visual shell of the
// call-for-fire surface — the live wiring is the plugin's job (out of scope here).
function FiresPane(): JSX.Element {
  return (
    <div className="slideout-plugin slideout-plugin--fires">
      <p className="slideout-plugin__note">
        Published by the Fires-Cell plugin. These fields are owned by the plugin, not
        the core COP.
      </p>
      <div className="slideout-plugin__placeholder">
        <span className="slideout-plugin__placeholder-tag">FIRES-CELL v2.1</span>
        <span className="slideout-plugin__placeholder-body">
          The call-for-fire surface comes online with the Fires-Cell plugin.
        </span>
      </div>
    </div>
  );
}

// ── EW plugin pane (visual shell) ─────────────────────────────────────────────
function EwPane(): JSX.Element {
  return (
    <div className="slideout-plugin slideout-plugin--ew">
      <p className="slideout-plugin__note">Published by the Spectrum/EW plugin.</p>
      <div className="slideout-plugin__placeholder">
        <span className="slideout-plugin__placeholder-tag">SPECTRUM v1.4</span>
        <span className="slideout-plugin__placeholder-body">
          The spectrum / EW emitter board comes online with the Spectrum plugin.
        </span>
      </div>
    </div>
  );
}

// ── CHAT pending (pre-boot) ───────────────────────────────────────────────────
// The chat pane needs the host's chatApi (threaded off handle.chatApi). Until startCop
// resolves it shows an honest "coming online" placeholder — never a blank dock.
function ChatPending(): JSX.Element {
  return (
    <div className="slideout-plugin">
      <div className="slideout-plugin__placeholder">
        <span className="slideout-plugin__placeholder-tag">CHAT · CONNECTING</span>
        <span className="slideout-plugin__placeholder-body">
          The encrypted mission-room chat comes online once the session is booted.
        </span>
      </div>
    </div>
  );
}

// ── AUDIT pane (append-only KAS verdict ledger) ───────────────────────────────
// Rule 4: KAS KEY RELEASED / KEY DENIED verdicts appear ONLY here (+ the Trust
// modal) — never as ambient chrome in the live picture. The host OBSERVES each
// terminal KAS/PDP decision into a bounded append-only ring (status.audit) without
// altering the spine; this pane renders those rows newest-first (mock lines 264–281):
// an 8px verdict dot + Saira verdict label + mono subject + "actor X · policy Y" meta
// + Zulu time. GRANT=grant / DENY=deny / REQUEST=accent. When no verdict has fired
// yet the pane shows the honest empty state — it NEVER fabricates rows (REQUEST rows
// in particular only ever appear if a real request path fires; today there is none).
function AuditPane({ status }: { status: CopStatus }): JSX.Element {
  const rows = status.audit;
  // ROUND-TRIP VERIFIED chip — DERIVED ONLY from the host's already-observed audit ledger
  // (each GRANT row = a live KAS rewrap that released a key and decrypted the payload —
  // shellContract.ts:AuditVerdict). It surfaces the EXISTING decrypt results; it makes NO
  // new decision and touches no pdp/kas/crypto. Honest: it only shows once ≥1 real round-trip
  // has actually happened, and counts exactly those grants.
  const decrypted = rows.reduce((n, r) => (r.verdict === "GRANT" ? n + 1 : n), 0);
  return (
    <div className="slideout-audit">
      {decrypted > 0 && (
        <div className="slideout-audit__roundtrip" title="Each count is a live KAS rewrap that released a key and decrypted a protected payload in-place.">
          <span className="slideout-audit__roundtrip-check" aria-hidden="true">✓</span>
          <span className="slideout-audit__roundtrip-label">KAS round-trip</span>
          <span className="slideout-audit__roundtrip-count">{decrypted} decrypted</span>
        </div>
      )}
      <p className="slideout-audit__note">
        Append-only ledger of every key release and denial. Verdicts live here — never
        in the live picture.
      </p>
      {rows.length === 0 ? (
        <div className="slideout-audit__placeholder">
          <span className="slideout-audit__placeholder-tag">LEDGER · NO VERDICTS YET</span>
          <span className="slideout-audit__placeholder-body">
            Every KAS key release and denial lands here the moment it happens. Select a
            protected contact to request its key — the verdict is recorded here, never
            in the picture.
          </span>
        </div>
      ) : (
        <div className="slideout-audit__rows">
          {rows.map((r, i) => (
            <div className="slideout-audit__row" key={`${r.time}:${r.subject}:${i}`}>
              <span className={`slideout-audit__dot slideout-audit__dot--${r.verdict.toLowerCase()}`} aria-hidden="true" />
              <div className="slideout-audit__body">
                <div className="slideout-audit__head">
                  <span className={`slideout-audit__verdict slideout-audit__verdict--${r.verdict.toLowerCase()}`}>
                    {r.verdict}
                  </span>
                  <span className="slideout-audit__subject">{r.subject}</span>
                </div>
                <div className="slideout-audit__meta">
                  actor {r.actor} · policy {r.policyRef}
                </div>
              </div>
              <span className="slideout-audit__time">{r.time}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
